import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import {
  REELS_SHORTS_BROLL_TYPES,
  brollTypeMeta,
  brollsForSnippet,
  orderedSnippets,
  positionsForBrollType,
  primaryBrollForSnippet,
  projectBrollVisualStyle,
} from '@/lib/reelsShortsBroll';
import {
  PAUSE_CUT_PRESETS,
  formatSecondsShort,
  pauseCutSummary,
  sanitizePauseCut,
} from '@/lib/reelsShortsPauseCut';
import { resolveReelsEditorCut } from '@/lib/reelsShortsEditor';
import { ReelsShortsBrollPreview, ReelsShortsBrollLookPicker } from '@/components/reelsShorts/ReelsShortsBrollPreview';
import { ReelsShortsTitlePicker } from '@/components/reelsShorts/ReelsShortsTitleTemplates';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const NONE = '__none__';

const DIAGRAM_KIND_LABELS = {
  avatar: 'Avatar',
  image: 'Image',
  ai_image: 'Image',
  stock_video: 'Stock video',
  storyboard: 'Storyboard',
  flowchart: 'Flowchart',
  mermaid_flowchart: 'Flowchart',
  mindmap: 'Mind map',
  diagram: 'Diagram',
  animation: 'Animation',
  text_animation: 'Text animation',
  cta: 'CTA',
};

function diagramInsertsForProject(project) {
  const cut = resolveReelsEditorCut(project);
  const diagrams = Array.isArray(cut?.diagram_inserts) ? cut.diagram_inserts : [];
  const avatars = Array.isArray(cut?.avatar_inserts) ? cut.avatar_inserts : [];
  return [...diagrams, ...avatars].filter((row) => row && typeof row === 'object');
}

function insertsAroundSnippet(inserts, snippetId) {
  const before = [];
  const after = [];
  for (const row of inserts) {
    if (String(row.before_snippet_id || '') === String(snippetId)) before.push(row);
    if (String(row.after_snippet_id || '') === String(snippetId)) after.push(row);
  }
  return { before, after };
}

function DiagramInsertCard({ row, placement }) {
  const kindKey = String(row.kind || row.type || '').toLowerCase();
  const kind = DIAGRAM_KIND_LABELS[kindKey] || kindKey || 'Scene';
  const dur = Number(row.duration_seconds) || 3;
  return (
    <div
      className="rounded-lg border border-cyan-400/20 bg-cyan-500/5 px-3 py-2 space-y-1"
      data-testid={`reels-shorts-diagram-insert-${row.id || placement}`}
    >
      <p className="text-[10px] uppercase tracking-wide text-cyan-300/90">
        {placement} · {kind} · {dur}s
      </p>
      <p className="text-xs font-semibold text-white truncate">
        {row.title || row.script || row.dialogue || 'Full scene'}
      </p>
      {Array.isArray(row.steps) && row.steps.length ? (
        <p className="text-[11px] text-slate-400 line-clamp-2">
          {row.steps.join(' → ')}
        </p>
      ) : row.generation_prompt ? (
        <p className="text-[11px] text-slate-400 line-clamp-2">{row.generation_prompt}</p>
      ) : null}
      {row.reason ? (
        <p className="text-[10px] text-slate-500 line-clamp-1">{row.reason}</p>
      ) : null}
    </div>
  );
}

const CLEANUP_LEVELS = [
  { value: 'natural', label: 'Natural', minPause: 0.7 },
  { value: 'balanced', label: 'Balanced', minPause: 0.45 },
  { value: 'aggressive', label: 'Aggressive', minPause: 0.3 },
];

function cleanupLevelFromPause(pauseCut) {
  if (!pauseCut?.enabled) return 'natural';
  const minP = Number(pauseCut.min_pause_seconds) || 0.45;
  if (minP <= 0.35) return 'aggressive';
  if (minP >= 0.6) return 'natural';
  return 'balanced';
}

function SnippetBrollStylePicker({
  projectId,
  snippet,
  currentType,
  currentPosition,
  saving,
  onChange,
}) {
  const typeId = currentType || NONE;
  const positions = positionsForBrollType(currentType);
  return (
    <div className="mt-3 flex flex-col sm:flex-row gap-2">
      <label className="flex-1 min-w-0 space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
          B-ROLL style
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        </span>
        <Select
          value={typeId}
          onValueChange={(next) => onChange(next === NONE ? '' : next, currentPosition)}
          disabled={saving || !projectId}
        >
          <SelectTrigger
            className="h-8 bg-black/40 border-[#2a3446] text-xs text-white"
            data-testid={`reels-shorts-broll-style-${snippet.id}`}
          >
            <SelectValue placeholder="No B-ROLL" />
          </SelectTrigger>
          <SelectContent className="z-[200]">
            <SelectItem value={NONE}>No B-ROLL</SelectItem>
            {REELS_SHORTS_BROLL_TYPES.map((row) => (
              <SelectItem key={row.id} value={row.id}>
                {row.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      {positions.length ? (
        <label className="sm:w-40 space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Position</span>
          <Select
            value={positions.includes(currentPosition) ? currentPosition : positions[0]}
            onValueChange={(next) => onChange(currentType, next)}
            disabled={saving || !projectId}
          >
            <SelectTrigger className="h-8 bg-black/40 border-[#2a3446] text-xs text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[200]">
              {positions.map((pos) => (
                <SelectItem key={pos} value={pos}>
                  {pos.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ) : null}
    </div>
  );
}

export function ReelsShortsEditorCutPanel({ project, onUpdated = null }) {
  const ordered = orderedSnippets(project);
  const cut = project?.editor_cut || {};
  const plan = project?.edit_plan || {};
  const analysis = plan.analysis || {};
  const rationale = cut.rationale || plan.story?.rationale || '';
  const projectId = project?.id || null;
  const pauseCut = sanitizePauseCut(cut.pause_cut || plan.cleanup?.pause_cut);
  const cleanupLevel = plan.cleanup?.level || cut.cleanup_level || cleanupLevelFromPause(pauseCut);
  const editingMode = plan.story?.editing_mode || cut.editing_mode || project?.editing_mode || 'auto';
  const diagramInserts = useMemo(() => diagramInsertsForProject(project), [project]);
  const summary = useMemo(
    () => pauseCutSummary(ordered, pauseCut.enabled ? pauseCut : { ...pauseCut, enabled: true }),
    [ordered, pauseCut.enabled, pauseCut.min_pause_seconds, pauseCut.pad_seconds],
  );
  const [savingId, setSavingId] = useState(null);
  const [savingCut, setSavingCut] = useState(false);
  const silenceScanRef = useRef('');
  const needsSilenceScan = ordered.some((snip) => snip?.silence_analyzed !== true);

  const patchCut = async (body, successMessage) => {
    if (!projectId || savingCut) return;
    setSavingCut(true);
    try {
      const { data } = await api.patch(`/reels-shorts/projects/${projectId}/cut`, body);
      if (data?.project && onUpdated) onUpdated(data.project);
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not update the cut'));
    } finally {
      setSavingCut(false);
    }
  };

  useEffect(() => {
    if (!projectId || savingCut || !needsSilenceScan) return;
    if (silenceScanRef.current === projectId) return;
    silenceScanRef.current = projectId;
    patchCut({ min_pause_seconds: pauseCut.min_pause_seconds });
    // patchCut is recreated each render; scan once per project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, needsSilenceScan]);

  const snippetIds = new Set(ordered.map((s) => String(s.id)));
  const standaloneScenes = diagramInserts.filter((row) => {
    const after = String(row.after_snippet_id || '');
    const before = String(row.before_snippet_id || '');
    return (!after || !snippetIds.has(after)) && (!before || !snippetIds.has(before));
  });

  if (!ordered.length && !diagramInserts.length) return null;

  const changeStyle = async (snippet, brollType, position) => {
    if (!projectId || savingId) return;
    setSavingId(snippet.id);
    try {
      const { data } = await api.patch(
        `/reels-shorts/projects/${projectId}/snippets/${snippet.id}/broll`,
        { broll_type: brollType || '', position: position || '' },
      );
      if (data?.project && onUpdated) onUpdated(data.project);
      toast.success(brollType ? 'B-ROLL style updated' : 'B-ROLL removed from this clip');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not change B-ROLL style'));
    } finally {
      setSavingId(null);
    }
  };

  const moveSnippet = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= ordered.length) return;
    const ids = ordered.map((row) => row.id);
    const swap = ids[next];
    ids[next] = ids[index];
    ids[index] = swap;
    patchCut({ ordered_snippet_ids: ids }, 'Cut order updated');
  };

  const presetValue = String(pauseCut.min_pause_seconds);
  const knownPreset = PAUSE_CUT_PRESETS.some((row) => String(row.minPause) === presetValue);

  return (
    <div className="space-y-4" data-testid="reels-shorts-editor-cut">
      {rationale ? (
        <div className="rounded-xl border border-rose-400/20 bg-rose-500/5 px-4 py-3 flex gap-2">
          <TwinneeLogo className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-slate-200 leading-relaxed">{rationale}</p>
            <p className="text-[10px] text-slate-500">
              Mode · {editingMode}
              {analysis.topic ? ` · Topic · ${analysis.topic}` : ''}
              {analysis.hook?.text ? ` · Hook · “${analysis.hook.text}”` : ''}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Generate the AI edit plan to place snippets in a logical order and suggest B-ROLL, or pick a style per clip below. Transcripts stay verbatim.
        </p>
      )}

      <div
        className="rounded-xl border border-[#1f2635] bg-[#12151d] p-4 space-y-3"
        data-testid="reels-shorts-pause-cut"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Scissors className="w-4 h-4 text-rose-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">AI cleanup / blank pauses</p>
              <p className="text-[11px] text-slate-500">
                Natural keeps breathing room; Aggressive jump-cuts silence. Spoken words stay verbatim.
              </p>
            </div>
          </div>
          <Switch
            checked={pauseCut.enabled}
            disabled={savingCut || !projectId}
            onCheckedChange={(next) => patchCut(
              { remove_blank_pauses: next },
              next ? 'Blank pauses will be cut from the reel' : 'Blank pauses kept',
            )}
            data-testid="reels-shorts-pause-cut-toggle"
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="sm:w-48 space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Cleanup level</span>
            <Select
              value={cleanupLevel}
              onValueChange={(next) => patchCut(
                { cleanup_level: next, remove_blank_pauses: true },
                `Cleanup set to ${next}`,
              )}
              disabled={savingCut || !projectId}
            >
              <SelectTrigger className="h-8 bg-black/40 border-[#2a3446] text-xs text-white" data-testid="reels-shorts-cleanup-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[200]">
                {CLEANUP_LEVELS.map((row) => (
                  <SelectItem key={row.value} value={row.value}>
                    {row.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="sm:w-48 space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Pause threshold</span>
            <Select
              value={knownPreset ? presetValue : '0.45'}
              onValueChange={(next) => patchCut(
                { min_pause_seconds: Number(next), remove_blank_pauses: true },
                'Pause threshold updated',
              )}
              disabled={savingCut || !projectId}
            >
              <SelectTrigger className="h-8 bg-black/40 border-[#2a3446] text-xs text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[200]">
                {PAUSE_CUT_PRESETS.map((row) => (
                  <SelectItem key={row.id} value={String(row.minPause)}>
                    {row.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <p className="text-[11px] text-slate-400 sm:pt-5">
            {needsSilenceScan || (savingCut && needsSilenceScan) ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Scanning clip audio for blank pauses…
              </span>
            ) : savingCut ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Saving cut…
              </span>
            ) : summary.removed_seconds >= 0.08 ? (
              `Cuts ${formatSecondsShort(summary.removed_seconds)} of silence · ${summary.pauses_removed} pause${summary.pauses_removed === 1 ? '' : 's'} across ${summary.clips_trimmed} clip${summary.clips_trimmed === 1 ? '' : 's'}`
            ) : (
              `No blank pauses longer than ${pauseCut.min_pause_seconds}s found in the clip audio`
            )}
          </p>
        </div>
        <p className="text-[11px] text-slate-500">
          Use the arrows on each clip to edit the cut order. Turn this on, then generate the final video or open the editor to play the jump-cut timeline.
        </p>
      </div>

      <ReelsShortsTitlePicker
        project={project}
        saving={savingCut}
        onSave={(titleOverlay, message) => patchCut({ title_overlay: titleOverlay }, message)}
      />

      <ReelsShortsBrollLookPicker
        value={projectBrollVisualStyle(project)}
        disabled={savingCut || !projectId}
        onChange={(next) => patchCut({ broll_visual_style: next }, 'B-ROLL visual style saved')}
      />

      {diagramInserts.length ? (
        <div
          className="rounded-xl border border-cyan-400/15 bg-cyan-500/[0.04] px-4 py-3 space-y-1"
          data-testid="reels-shorts-diagram-inserts-summary"
        >
          <p className="text-sm font-semibold text-white">
            {diagramInserts.length} full scene{diagramInserts.length === 1 ? '' : 's'} on the reel
          </p>
          <p className="text-[11px] text-slate-500">
            Avatar, image, stock video, flowchart, mind map, text animation, and CTA compose on the reels_shorts queue with your clips.
          </p>
        </div>
      ) : null}

      <ol className="space-y-3">
        {!ordered.length && standaloneScenes.map((row) => (
          <li key={row.id || row.title}>
            <DiagramInsertCard row={row} placement="Full scene" />
          </li>
        ))}
        {ordered.length ? standaloneScenes.map((row) => (
          <li key={`standalone-${row.id || row.title}`}>
            <DiagramInsertCard row={row} placement="Full scene" />
          </li>
        )) : null}
        {ordered.map((snip, idx) => {
          const brolls = brollsForSnippet(project, snip.id);
          const primary = primaryBrollForSnippet(project, snip.id);
          const { before, after } = insertsAroundSnippet(diagramInserts, snip.id);
          return (
            <li
              key={snip.id}
              className="rounded-xl border border-[#1f2635] bg-[#12151d] p-4 space-y-2"
            >
              {before.map((row) => (
                <DiagramInsertCard key={row.id || `before-${snip.id}`} row={row} placement="Before clip" />
              ))}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-[10px] font-mono text-rose-300">#{idx + 1}</span>
                <h3 className="text-sm font-semibold text-white truncate">
                  {snip.filename || 'Clip'}
                </h3>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    className="h-7 w-7 rounded-md border border-white/10 text-slate-400 hover:text-white disabled:opacity-30"
                    disabled={savingCut || idx === 0}
                    onClick={() => moveSnippet(idx, -1)}
                    title="Move earlier in the cut"
                    data-testid={`reels-shorts-cut-up-${snip.id}`}
                  >
                    <ChevronUp className="w-3.5 h-3.5 mx-auto" />
                  </button>
                  <button
                    type="button"
                    className="h-7 w-7 rounded-md border border-white/10 text-slate-400 hover:text-white disabled:opacity-30"
                    disabled={savingCut || idx === ordered.length - 1}
                    onClick={() => moveSnippet(idx, 1)}
                    title="Move later in the cut"
                    data-testid={`reels-shorts-cut-down-${snip.id}`}
                  >
                    <ChevronDown className="w-3.5 h-3.5 mx-auto" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
                {snip.transcript || <span className="text-slate-600 italic">No speech</span>}
              </p>
              <SnippetBrollStylePicker
                projectId={projectId}
                snippet={snip}
                currentType={primary?.broll_type || ''}
                currentPosition={primary?.position || ''}
                saving={savingId === snip.id}
                onChange={(type, position) => changeStyle(snip, type, position)}
              />
              {brolls.length ? (
                <ul className="mt-3 space-y-2">
                  {brolls.map((sug) => {
                    const meta = brollTypeMeta(sug.broll_type);
                    return (
                      <li
                        key={sug.id}
                        className="flex gap-3 rounded-lg border border-white/5 bg-black/30 p-2.5"
                      >
                        <ReelsShortsBrollPreview suggestion={sug} />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-amber-200">
                            {meta?.label || sug.broll_type}
                            {sug.position ? ` · ${sug.position}` : ''}
                          </p>
                          {sug.trigger_text ? (
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              When spoken: “{sug.trigger_text}”
                            </p>
                          ) : null}
                          {sug.generation_prompt ? (
                            <p className="text-[11px] text-slate-300 mt-1">
                              Prompt: {sug.generation_prompt}
                            </p>
                          ) : null}
                          {sug.notes ? (
                            <p className="text-[11px] text-slate-500 mt-1">{sug.notes}</p>
                          ) : null}
                          {Array.isArray(sug.cards) && sug.cards.length ? (
                            <p className="text-[11px] text-slate-400 mt-1">
                              {sug.cards.length} card{sug.cards.length === 1 ? '' : 's'} along the bottom — highlighted when its line plays.
                            </p>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {after.map((row) => (
                <DiagramInsertCard key={row.id || `after-${snip.id}`} row={row} placement="After clip" />
              ))}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
